/**
 * Shoe painting — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const shoePaintingPack: ToilePack = {
    slug: 'shoe-painting',
    category: 'craft',
    name: 'Shoe painting',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'floral', label: 'Floral', subject: 'white sneakers painted with flowers'},
        {key: 'anime', label: 'Anime', subject: 'sneakers painted with an anime character'},
        {key: 'graffiti', label: 'Graffiti', subject: 'sneakers painted with graffiti letters reading "FRESH"'},
    ],
    styles: [
        {key: 'clean', label: 'Clean', style: 'neat leather acrylic painting with crisp edges and a matte finish', palette: 'soft pastels on bright white leather', framing: 'a single sneaker in side profile on a white plinth against a light grey wall'},
        {key: 'bold', label: 'Bold', style: 'thick saturated leather paint with black outlines under a glossy finish coat', palette: 'vivid red, electric blue and yellow on white', framing: 'a pair side by side on a concrete floor, a three-quarter view from above'},
        {key: 'drip', label: 'Drip', style: 'paint splatters and drips layered over the main design', palette: 'neon pink, lime and black over white', framing: 'a pair on a wooden crate beside open paint pots and brushes'},
    ],
    directions: ['canvas shoes', 'galaxy', 'for a wedding'],
    directionHint: 'try: canvas shoes',
    seo: {
        title: 'Shoe painting: floral, anime, graffiti sneakers',
        metaDescription: 'Nine shoe painting ideas on white sneakers: floral, anime and graffiti designs in clean, bold and drip finishes.',
        h1: 'Shoe painting ideas: floral, anime, graffiti sneakers',
        tagline: 'Three sneaker designs, each in a clean, a bold and a drip finish.',
        intro: 'White sneakers are a plain base for paint, and the design sets the look. These nine show three sneaker designs: flowers, an anime character and graffiti letters reading FRESH. Each is painted in a clean matte finish, a bold finish with black outlines or a drip finish, and shown in side profile, as a pair on a concrete floor, or on a crate beside the paint. Type a subject, such as galaxy, to ask for it.',
        useCases: [
            'a custom pair for everyday wear',
            'a gift for a friend who likes sneakers',
            'a school or college art project',
            'a pair for a party outfit',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Floral, anime and graffiti sneakers, each in a clean, bold and drip finish.'},
            {q: 'Are these photos of real shoes?', a: 'No. They are generated pictures of each design, not photographs of shoes someone painted.'},
            {q: 'What is the difference between the finishes?', a: 'Clean is neat with a matte finish and soft pastels. Bold is thick, saturated paint with black outlines under a glossy coat. Drip has paint splatters and drips over the main design.'},
            {q: 'Can I ask for a different design?', a: 'Yes. Type a subject such as galaxy or canvas shoes. A typed subject is a request, not a promised result.'},
        ],
    },
}
